import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { ToastMessages } from "../../toasts/toastMessages";
import SideModal from "../filterModal/filterModal";
import AddCodeSection from "./components/AddCodeSection";
import DrugTypeSelector from "./components/DrugTypeSelector";
import DxCodesList from "./components/DxCodesList";
import ModalFooter from "./components/ModalFooter";
import ModalHeader from "./components/ModalHeader";
import { DxSourceTypeOption } from "./dxCodeRowTagOptions";
import "./editDxCodeSidePanel.scss";

export interface DxCodeEntry {
  /** Stable React key per row; not persisted to Firestore */
  id: string;
  dxCode: string;
  /**
   * Priority value, persisted as the per-code `rank` on a per-org drug config
   * (the engine orders by rank before recency). Undefined when unranked.
   */
  priority?: number;
  source?: string[];
}

/** Payload shape from config (no row id until the edit panel assigns one). */
export type DxCodeEntryInput = Omit<DxCodeEntry, "id">;

const assignRowIds = (entries: DxCodeEntryInput[]): DxCodeEntry[] =>
  entries.map((e) => ({
    ...e,
    id: crypto.randomUUID(),
  }));

interface EditDxCodeSidePanelProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: EditDxCodeData) => void;
  mode: "drug" | "regimen";
  // Drug mode props
  drugName?: string;
  jCode?: string;
  drugType?: string;
  // Regimen mode props
  regimenName?: string;
  regimenCode?: string;
  // Common props
  dxCodes: DxCodeEntryInput[];
  selectedOrgId: string;
  isSaving?: boolean;
  hasPriorityConfig?: boolean;
  sourceTypeOptions: readonly DxSourceTypeOption[];
}

export interface EditDxCodeData {
  id: string;
  mode: "drug" | "regimen";
  drugType?: string;
  dxCodes: DxCodeEntryInput[];
  selectedOrgId: string;
}

const EditDxCodeSidePanel = memo((props: EditDxCodeSidePanelProps) => {
  const [drugType, setDrugType] = useState<string>(props.drugType || "");
  const [dxCodes, setDxCodes] = useState<DxCodeEntry[]>(() =>
    assignRowIds(props.dxCodes || []),
  );
  const [searchTerm, setSearchTerm] = useState<string>("");
  const prevIsOpenRef = useRef<boolean>(props.isOpen);

  useEffect(() => {
    // Only reset state when modal transitions from closed to open
    if (props.isOpen && !prevIsOpenRef.current) {
      setDrugType(props.drugType || "");
      setDxCodes(assignRowIds(props.dxCodes || []));
      setSearchTerm("");
    }
    prevIsOpenRef.current = props.isOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isOpen]);

  const handleClose = useCallback(() => {
    props.onClose();
  }, [props.onClose]);

  const handleSave = useCallback(() => {
    const id =
      props.mode === "drug" ? props.jCode || "" : props.regimenCode || "";
    const dxCodesForSave: DxCodeEntryInput[] = dxCodes.map(
      ({ id: _rowId, ...rest }) => rest,
    );
    props.onSave({
      id,
      mode: props.mode,
      drugType: props.mode === "drug" ? drugType : undefined,
      dxCodes: dxCodesForSave,
      selectedOrgId: props.selectedOrgId,
    });
  }, [
    props.onSave,
    props.mode,
    props.jCode,
    props.regimenCode,
    props.selectedOrgId,
    drugType,
    dxCodes,
  ]);

  const handleAddCode = useCallback((newCode: DxCodeEntryInput) => {
    setDxCodes((prev) => {
      // Check if code already exists using the current state
      const codeExists = prev.some(
        (entry) => entry.dxCode.toLowerCase() === newCode.dxCode.toLowerCase(),
      );
      if (!codeExists) {
        toast.success(ToastMessages.DX_CODE_ADDED_SUCCESS);
        return [...prev, { ...newCode, id: crypto.randomUUID() }];
      } else {
        toast.error(ToastMessages.DX_CODE_DUPLICATE_ERROR);
        return prev;
      }
    });
  }, []);

  const handleDeleteCode = useCallback((rowId: string) => {
    setDxCodes((prev) => {
      toast.success(ToastMessages.DX_CODE_DELETED_SUCCESS);
      return prev.filter((e) => e.id !== rowId);
    });
  }, []);

  const handleDxCodeChange = useCallback((rowId: string, value: string) => {
    setDxCodes((prev) =>
      prev.map((entry) =>
        entry.id === rowId ? { ...entry, dxCode: value } : entry,
      ),
    );
  }, []);

  const handleTypeChange = useCallback((rowId: string, value: string[]) => {
    setDxCodes((prev) =>
      prev.map((entry) =>
        entry.id === rowId ? { ...entry, source: value } : entry,
      ),
    );
  }, []);

  const handlePriorityChange = useCallback((rowId: string, value: string) => {
    const trimmedValue = value.trim();
    // Handle "-", empty string, or invalid numbers as undefined
    if (!trimmedValue || trimmedValue === "-") {
      setDxCodes((prev) =>
        prev.map((entry) =>
          entry.id === rowId ? { ...entry, priority: undefined } : entry,
        ),
      );
      return;
    }
    const priority = parseInt(trimmedValue, 10);
    // If parsing results in NaN, set priority to undefined
    const finalPriority = isNaN(priority) ? undefined : priority;
    setDxCodes((prev) =>
      prev.map((entry) =>
        entry.id === rowId ? { ...entry, priority: finalPriority } : entry,
      ),
    );
  }, []);

  const filteredDxCodes = dxCodes.filter((entry) => {
    if (!searchTerm.trim()) {
      return true;
    }
    const searchLower = searchTerm.toLowerCase();
    const typeMatch = (entry.source ?? []).some((s) =>
      s.toLowerCase().includes(searchLower),
    );
    return (
      entry.dxCode.toLowerCase().includes(searchLower) ||
      (entry.priority !== undefined &&
        entry.priority.toString().includes(searchTerm)) ||
      typeMatch
    );
  });

  const title =
    props.mode === "drug"
      ? `${props.jCode || ""} ${props.drugName || ""}`.trim()
      : props.regimenName || "";

  return (
    <SideModal
      isOpen={props.isOpen}
      onClose={handleClose}
      id="edit-dx-code-side-panel"
    >
      <div className="flex h-full flex-col bg-white">
        <ModalHeader title={title} onClose={handleClose} />

        <div className="flex-1 overflow-y-auto p-4">
          {props.mode === "drug" && (
            <DrugTypeSelector
              drugType={drugType}
              onDrugTypeChange={setDrugType}
            />
          )}

          <div className="mb-6">
            <AddCodeSection
              key={props.isOpen ? "open" : "closed"}
              onAddCode={handleAddCode}
              onSearchChange={setSearchTerm}
              searchTerm={searchTerm}
              options={props.sourceTypeOptions}
            />
          </div>

          <DxCodesList
            filteredDxCodes={filteredDxCodes}
            dxCodes={dxCodes}
            hasPriorityConfig={props.hasPriorityConfig}
            onDxCodeChange={handleDxCodeChange}
            onPriorityChange={handlePriorityChange}
            onTypeChange={handleTypeChange}
            onDeleteCode={handleDeleteCode}
            options={props.sourceTypeOptions}
          />
        </div>

        <ModalFooter
          onClose={handleClose}
          onSave={handleSave}
          isSaving={props.isSaving}
        />
      </div>
    </SideModal>
  );
});

EditDxCodeSidePanel.displayName = "EditDxCodeSidePanel";

export default EditDxCodeSidePanel;
